import { ICommonAuthResp, IScopeResp, Group } from '@zero-dash/types';
import { AxiosError, AxiosInstance } from 'axios';
import { Role } from '@/types/Auth/Role.types';
import { Scope } from '@/types/Auth/Scope.types';
import { IUserFilters, IUserWithMetadata } from '@/types/Auth/User.types';

export interface IAuthClient {
  getCurrentUser(): Promise<IUserWithMetadata | null | undefined>;
  getUserById(id: string): Promise<IUserWithMetadata>;
  getUserByAzureId(id: string): Promise<IUserWithMetadata>;
  getUsers(filters?: IUserFilters): Promise<IUserWithMetadata[]>;
  getUsersByGroupId(groupId: string): Promise<IUserWithMetadata[]>;
  getUsersByRoleId(roleId: string): Promise<IUserWithMetadata[]>;

  getRoles(): Promise<ICommonAuthResp<Role>[]>;
  getScopesByRoleId(roleId: string): Promise<IScopeResp<Scope>[]>;

  getGroups(): Promise<ICommonAuthResp<Group>[]>;

  getApplications(name?: string): Promise<ICommonAuthResp<string>[]>;
}

export function createAuthClient(instance: AxiosInstance): IAuthClient {
  async function getCurrentUser(): Promise<IUserWithMetadata | null | undefined> {
    try {
      const resp = await instance.get('/auth/users/me');

      return resp.data;
    } catch (e) {
      if ((e as AxiosError).code !== 'ERR_CANCELED') return null;
      return undefined;
    }
  }

  async function getUserById(id: string): Promise<IUserWithMetadata> {
    const resp = await instance.get(`/auth/users/${id}`);

    return resp.data;
  }

  async function getUserByAzureId(azureId: string): Promise<IUserWithMetadata> {
    const resp = await instance.get(`/auth/users/azure/${azureId}`);

    return resp.data;
  }

  async function getUsers(filters: IUserFilters): Promise<IUserWithMetadata[]> {
    const resp = await instance.get('/auth/users', {
      params: filters,
    });

    return resp.data;
  }

  async function getUsersByGroupId(groupId: string): Promise<IUserWithMetadata[]> {
    const resp = await instance.get(`/auth/groups/${groupId}/users`);

    return resp.data;
  }

  async function getUsersByRoleId(roleId: string): Promise<IUserWithMetadata[]> {
    const resp = await instance.get(`/auth/roles/${roleId}/users`);

    return resp.data;
  }

  async function getRoles(): Promise<ICommonAuthResp<Role>[]> {
    const resp = await instance.get('/auth/roles');

    return resp.data;
  }

  async function getScopesByRoleId(roleId: string): Promise<IScopeResp<Scope>[]> {
    const resp = await instance.get(`/auth/roles/${roleId}/scopes`);

    return resp.data;
  }

  async function getGroups(): Promise<ICommonAuthResp<Group>[]> {
    const resp = await instance.get('/auth/groups');

    return resp.data;
  }

  async function getApplications(name?: string): Promise<ICommonAuthResp<string>[]> {
    const resp = await instance.get('/auth/applications', { params: { name } });

    return resp.data;
  }

  return {
    getCurrentUser,
    getUserById,
    getUserByAzureId,
    getUsers,
    getUsersByGroupId,
    getUsersByRoleId,

    getRoles,
    getScopesByRoleId,

    getGroups,

    getApplications,
  };
}
